import { AGWASuiteApiClientBase, ApiResponse } from '../agwasuite-api';
import type {
  AddGroupInput,
  AddGroupResult,
  AddGroupUserInput,
  AddGroupUserResult,
  EditGroupInput,
  EditGroupUserInput,
  GetGroupsResult,
  GetGroupUsersInput,
  GetGroupUsersResult,
  SetGroupBlockedInput,
  SetGroupBlockedResult,
  SetGroupUserBlockedInput,
  SetGroupUserBlockedResult,
  LoginGroup,
  LoginUser,
  LoginUserImis,
} from '../agwa-api-model';
import { toMembershipStatus } from '../../pages/app-members/shared/company-status-display';

export class ApiUserMaintenance extends AGWASuiteApiClientBase {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }

  async getGroupUsers(
    input: GetGroupUsersInput
  ): Promise<ApiResponse<GetGroupUsersResult>> {
    return this.postExecute<GetGroupUsersInput, GetGroupUsersResult>(
      '/api/usermaintenance/GetGroupUsers',
      input
    );
  }

  async getGroups(): Promise<ApiResponse<GetGroupsResult>> {
    return this.postExecute<{}, GetGroupsResult>(
      '/api/usermaintenance/GetGroups',
      {}
    );
  }

  async addGroupUser(
    input: AddGroupUserInput
  ): Promise<ApiResponse<AddGroupUserResult>> {
    return this.postExecute<AddGroupUserInput, AddGroupUserResult>(
      '/api/usermaintenance/AddGroupUser',
      input
    );
  }
  /**
   * Saves a person's details. `user` carries the version the page loaded (concurrencyToken), and a
   * save from a page opened before somebody else's save is refused 409 (#398 C18, @LOCK-057).
   * Whether the person is blocked is not changed here; that is setGroupUserBlocked.
   */
  async editGroupUser(
    user: LoginUser,
    imis: LoginUserImis | null
  ): Promise<ApiResponse<AddGroupUserResult>> {
    return this.postExecute<EditGroupUserInput, AddGroupUserResult>(
      '/api/usermaintenance/EditGroupUser',
      { user, imis: imis ?? undefined }
    );
  }

  /** Blocks a person, or activates a blocked one, changing nothing else (#398 C18, @LOCK-059). */
  async setGroupUserBlocked(
    input: SetGroupUserBlockedInput
  ): Promise<ApiResponse<SetGroupUserBlockedResult>> {
    return this.postExecute<
      SetGroupUserBlockedInput,
      SetGroupUserBlockedResult
    >('/api/usermaintenance/SetGroupUserBlocked', input);
  }

  // --- Group Management Methods ---

  async addGroup(input: AddGroupInput): Promise<ApiResponse<AddGroupResult>> {
    return this.postExecute<AddGroupInput, AddGroupResult>(
      '/api/usermaintenance/AddGroup',
      input
    );
  }

  /**
   * Blocks a company, or unblocks a blocked one, changing nothing else (#406, @LOCK-066). Made under
   * the company's edit lock, which the server takes for the change and releases after it; the page
   * takes nothing (@LOCK-038, @LOCK-070).
   */
  async setGroupBlocked(
    input: SetGroupBlockedInput
  ): Promise<ApiResponse<SetGroupBlockedResult>> {
    return this.postExecute<SetGroupBlockedInput, SetGroupBlockedResult>(
      '/api/usermaintenance/SetGroupBlocked',
      input
    );
  }

  /**
   * Saves a company's details. Whether it is blocked is not changed here, whatever `active` says:
   * the server ignores it (#406, @LOCK-069); that is setGroupBlocked.
   */
  async editGroup(input: LoginGroup): Promise<ApiResponse<AddGroupResult>> {
    // Map LoginGroup (with id) to EditGroupInput (with loginGroupId)
    const editInput: EditGroupInput = {
      loginGroupId: input.id,
      name: input.name,
      active: input.active,
      // Carried, so a company's membership can be corrected after it is added. Until this line
      // existed the field was write-once at creation: the server accepted it on edit and nothing
      // ever sent it, which made Story 2 of the acceptance document true of the API and false of
      // every screen. Sending the current value on an unrelated edit is a no-op server-side, since
      // JobEditGroup only assigns when the value actually differs.
      //
      // Normalised on the way out because the value being echoed back may be the camelCase form the
      // server actually sends (see toMembershipStatus). Reading it back happens to be forgiving
      // today, and relying on that leniency is how a working call becomes a 400 the day it is
      // tightened.
      membershipStatus: toMembershipStatus(input.membershipStatus),
      userNamePrefix: input.userNamePrefix,
    };
    return this.postExecute<EditGroupInput, AddGroupResult>(
      '/api/usermaintenance/EditGroup',
      editInput
    );
  }
}
